import {
    APP_INITIALIZER,
    ApplicationConfig,
    importProvidersFrom,
    inject,
    PLATFORM_ID,
    provideExperimentalZonelessChangeDetection
} from '@angular/core';
import {NoPreloading, provideRouter, withInMemoryScrolling, withPreloading} from '@angular/router';
import {routes} from './app.routes';
import {
    BrowserModule,
    provideClientHydration,
    withHttpTransferCacheOptions,
    withI18nSupport
} from '@angular/platform-browser';
import {getApp, initializeApp, provideFirebaseApp} from '@angular/fire/app';
import {getAuth, provideAuth} from '@angular/fire/auth';
import {getAnalytics, provideAnalytics, ScreenTrackingService, UserTrackingService} from '@angular/fire/analytics';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {initializeAppCheck, provideAppCheck, ReCaptchaV3Provider} from '@angular/fire/app-check';
import {environment} from '../environments/environment';
import {provideHttpClient, withFetch, withInterceptors} from '@angular/common/http';
import {cacheInterceptor} from './core/interceptor/cache.interceptor';
import {ModalModule} from 'ngx-bootstrap/modal';
import {isPlatformServer} from '@angular/common';
import {UserService} from "./core/services/user.service";
import {GeoService} from "./core/services/geo.service";
import {geoInterceptor} from "./core/interceptor/geo.interceptor";

export function initializeAppUser(userService: UserService) {
    return () => userService.initializeUser();
}

export function initializeGeo(geoService: GeoService) {
    return () => geoService.initialize();
}

export const appConfig: ApplicationConfig = {
    providers: [
        // Import essential modules
        importProvidersFrom(BrowserModule, ModalModule.forRoot()),

        provideExperimentalZonelessChangeDetection(),

        // Set up HTTP client with fetch and caching
        provideHttpClient(
            withFetch(),
            withInterceptors([cacheInterceptor, geoInterceptor])
        ),

        // Configure routing with no preloading for dynamic content
        provideRouter(
            routes,
            withPreloading(NoPreloading),
            withInMemoryScrolling({
                scrollPositionRestoration: 'top',
                anchorScrolling: 'enabled'
            })
        ),

        // Enable hydration for SSR
        provideClientHydration(
            (withHttpTransferCacheOptions({
                includeHeaders: [
                    'x-pagination-current-page',
                    'x-pagination-total-count',
                    'x-pagination-page-count',
                    'x-pagination-current-page',
                ]
            })),
            withI18nSupport()
        ),

        // Initialize Firebase with lazy loading
        provideFirebaseApp(() => {
            const platformId = inject(PLATFORM_ID);
            if (isPlatformServer(platformId)) {
                return undefined as any;
            }
            return initializeApp(environment.firebaseConfig);
        }),

        // Configure App Check for client-side only with lazy loading
        provideAppCheck(() => {
            const platformId = inject(PLATFORM_ID);
            if (isPlatformServer(platformId)) {
                return undefined as any;
            }

            return initializeAppCheck(getApp(), {
                provider: new ReCaptchaV3Provider(environment.appCheckSiteKey),
                isTokenAutoRefreshEnabled: true,
            });
        }),

        // Provide Firebase services with lazy loading
        provideAuth(() => {
            if (typeof window !== 'undefined') {
                return getAuth();
            }
            return undefined as any;
        }),
        provideAnalytics(() => {
            if (typeof window !== 'undefined') {
                return getAnalytics();
            }
            return undefined as any;
        }),
        ScreenTrackingService,
        UserTrackingService,
        // Provide animations asynchronously
        provideAnimationsAsync(),
        GeoService,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeGeo,
            deps: [GeoService],
            multi: true
        },
        // Initialize user service
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAppUser,
            deps: [UserService],
            multi: true
        },
    ]
};
